import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import doneImg from "../../../assets/images/passangerData/done.png";
import CustomLoading from "../../GeneralComponents/CustomLoading/customloading";
import { parseNumber } from "../../../services/currencyService";
import { PaymentsAmountInput } from "./CardPaymentInputs";
import PaymentInterest from "./PaymentInterest";
import PaymentIcons from "./PaymentIcons";
import { handleInputChange } from "../../../utils/PaymentHelpers";
import "./CardPayment.scss";
import CardPaidOverlay from "./CardPaidOverlay";
// import { Loader } from "../../Loader/Loader";
import { useCards } from "../../../contexts/CardsContext";
import { Button, message, Popconfirm, Modal } from "antd";
import { getLdsOrder } from "../../../services/fhpService";
import { getUserDetails, isAdminUser } from "../../../services/authService";
import { getDynamicOrder } from "../../../services/dynamicService";
import {
  executeLdsCreditPaymentLink,
  getFirstUnpaidLdsPaymentUrl,
} from "../../../utils/executeLdsCreditPaymentLink";
import {
  CREDIT_PAY_NOW_LABEL,
  CREDIT_PAY_PROCESSING_LABEL,
  creditPayCreateActionLabel,
  creditPayCreateLoadingLabel,
} from "../../../utils/packageProfileCustomerPayment";
import { useNavigate } from "react-router-dom";
import { ApiReqBaseUrl } from "../../../assets/GlobalVariables";
import axios from "axios";
import {
  trackMetaAddPaymentInfo,
  trackMetaInitiateCheckout,
} from "../../../utils/metaPixel/metaPixelAnalytics";
import {
  classifyHypIframeHref,
  formatSequentialIls,
  isLdsPaymentSlotFailed,
  isLdsPaymentSlotPaid,
  isOrderFullyPaidForConfirmation,
  resolvePaymentSlotFromOrder,
} from "../../../utils/payment/sequentialMultiCardPayment";
import { completePublicVacationPackageBooking } from "../../../features/publicBookingConfirmation/completePublicVacationPackageBooking";

const CardPayment = ({
  cardIndex,
  paymentLink,
  currentOrderId,
  setOrderReqData,
  selectedResult,
  /** `boolean` = enforce (packages/profile); `undefined` = no gate (e.g. organized tour). */
  paymentTermsAccepted,
  onPaymentTermsBlocked,
  /** כיתות ל־triggerPackageProfileCreditPay מסרגל הצד */
  packageProfileCreditMarkers = false,
  /** `lds` (חבילות נופש LDS) | `dynamic` (חבילה דינמית / טיסות סוכן). */
  creditPaymentApi = "lds",
  /** חסימת יצירת קישור כשחלוקת כרטיסים לא שווה ליתרה (טיסות סוכן). */
  splitPaymentError = null,
  afterPayLinkSlot = null,
  beforeCreatePaymentLink = null,
  /** Public/customer: create HYP link then open pay immediately. Agent keeps copy-link. */
  instantOpenPay = false,
  /** Holiday checkout: keep every split-card form open for the horizontal row. */
  forceExpanded = false,
  sequentialMode = false,
  requiredOrderTotalIls = null,
  onSequentialProcessingChange = null,
  onSequentialFailed = null,
  onSequentialSlotPaid = null,
  completeToPublicConfirmation = false,
}) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const { cards, updateCards } = useCards();
  const { t } = useTranslation();
  const [currentError, setCurrentError] = useState(false);
  const [expandCard, setExpandCard] = useState(
    () =>
      Boolean(forceExpanded) ||
      (Boolean(packageProfileCreditMarkers) && cardIndex === 0)
  );
  // const [loading, setLoading] = useState(false);
  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);
  const [instantPayUrl, setInstantPayUrl] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [paymentLinkModalVisible, setPaymentLinkModalVisible] = useState(false);
  const [hasShownPaymentUpdateError, setHasShownPaymentUpdateError] = useState(false);
  const [hasShownPaymentNotClosed, setHasShownPaymentNotClosed] = useState(false);
  const [slotFailed, setSlotFailed] = useState(false);
  const [confirmingFromProvider, setConfirmingFromProvider] = useState(false);
  const payLockRef = useRef(false);
  const currentCard = cards[cardIndex];
  const { paid: currentCardPaid } = currentCard;
  const cardDisabled = currentCardPaid;

  const paymentTermsBlocking =
    typeof paymentTermsAccepted === "boolean" && !paymentTermsAccepted;
  const hasOrderOrSync =
    Boolean(currentOrderId) || typeof beforeCreatePaymentLink === "function";
  const createPayCtaLabel = sequentialMode
    ? `שלם ${formatSequentialIls(currentCard?.amount)}`
    : creditPayCreateActionLabel({
        instantOpenPay,
        createLinkLabel: t("paymentagent.create-pay-link"),
      });
  const createPayLoadingLabel = sequentialMode
    ? CREDIT_PAY_PROCESSING_LABEL
    : creditPayCreateLoadingLabel({
        instantOpenPay,
        createLinkLabel: t("paymentagent.create-pay-link"),
      });

  const openPayInSameWindow = (url) => {
    const href = url != null ? String(url).trim() : "";
    if (!href) return false;
    setInstantPayUrl(href);
    setExpandCard(true);
    setPaymentLinkOpen(true);
    return true;
  };

  const fetchOrderPaymentSnapshot = React.useCallback(async () => {
    if (!currentOrderId) return { data: null };
    if (creditPaymentApi === "dynamic") {
      const agentId = getUserDetails()?.id;
      if (!agentId) return { data: null };
      const res = await getDynamicOrder({
        id: currentOrderId,
        agentID: agentId,
        isAdmin: isAdminUser() ? 1 : 0,
      });
      return { data: res?.data ?? null };
    }
    return getLdsOrder({ id: currentOrderId });
  }, [creditPaymentApi, currentOrderId]);

  const markSlotFailed = (reason = "provider") => {
    setSlotFailed(true);
    setConfirmingFromProvider(false);
    payLockRef.current = false;
    onSequentialProcessingChange?.(null);
    onSequentialFailed?.(cardIndex);
    setPaymentLinkOpen(false);
    if (sequentialMode) {
      console.info("[sequential_payment_failed]", { orderId: currentOrderId, cardIndex, reason });
    }
  };

  const applyConfirmedPaidOrder = async (order) => {
    if (!order) return false;
    const slot = resolvePaymentSlotFromOrder(order, cardIndex, paymentLink);
    if (!isLdsPaymentSlotPaid(slot, order, cardIndex)) {
      if (sequentialMode && isLdsPaymentSlotFailed(slot)) {
        markSlotFailed("slot_failed");
      }
      return false;
    }

    setSlotFailed(false);
    onSequentialFailed?.(null);
    setConfirmingFromProvider(false);
    setPaymentLinkOpen(false);
    setOrderReqData(order);
    payLockRef.current = false;
    onSequentialProcessingChange?.(null);
    onSequentialSlotPaid?.(order, cardIndex);
    message.success(t("notifications.paymentSuccessful"));

    if (isOrderFullyPaidForConfirmation(order, requiredOrderTotalIls)) {
      try {
        const allPayments = order.payments || [];
        const paidPayments = allPayments.filter((p) => p.paid);
        const totalAmountPaid = paidPayments.reduce(
          (sum, p) => sum + (parseFloat(p.actualAmount ?? p.amount) || 0),
          0
        );
        const totalNumberOfPayments = paidPayments.reduce(
          (sum, p) => sum + (parseInt(p.tash, 10) || 1),
          0
        );
        await axios.put(
          ApiReqBaseUrl + `/orders/${currentOrderId}`,
          {
            totalAmountPaid,
            totalNumberOfPayments,
            numberOfCreditCards: paidPayments.length,
            status: "paid",
            sumpaid: totalAmountPaid,
          },
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
            validateStatus: () => true,
          }
        );
      } catch (err) {
        notifyPaymentUpdateFailure({ stage: "update_summary", error: err?.message });
      }

      const fresh = await fetchOrderPaymentSnapshot().catch(() => null);
      const confirmed = fresh?.data || order;
      if (!isOrderFullyPaidForConfirmation(confirmed, requiredOrderTotalIls)) {
        setOrderReqData(confirmed);
        return true;
      }

      if (completeToPublicConfirmation) {
        const done = completePublicVacationPackageBooking({
          order: confirmed,
          payableIls: requiredOrderTotalIls,
          paymentMethod: "credit",
        });
        if (done?.href) {
          navigate(done.href, { replace: true });
        }
      }
    }
    return true;
  };

  useEffect(() => {
    if (cardIndex !== 0 || !currentOrderId) return;
    let cancelled = false;
    fetchOrderPaymentSnapshot()
      .then((res) => {
        if (cancelled || !res?.data) return;
        trackMetaInitiateCheckout(res.data, currentOrderId);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cardIndex, currentOrderId, fetchOrderPaymentSnapshot]);

  const notifyTermsRequired = () => {
    onPaymentTermsBlocked?.();
    message.info("יש לאשר את התקנון כדי להמשיך לתשלום");
  };

  const emitTelemetryEvent = (eventName, payload = {}) => {
    try {
      if (window.gtag) {
        window.gtag("event", eventName, payload);
      }
      if (window.Sentry && typeof window.Sentry.captureMessage === "function") {
        window.Sentry.captureMessage(eventName, { level: "info", extra: payload });
      }
    } catch (e) {
      // Ignore telemetry failures
    }
    console.info("[telemetry]", eventName, payload);
  };

  const notifyPaymentUpdateFailure = (context = {}) => {
    if (!hasShownPaymentUpdateError) {
      message.warning(
        "התשלום התקבל אך עדכון סטטוס ההזמנה נכשל. נא לרענן או לפנות לתמיכה."
      );
      setHasShownPaymentUpdateError(true);
    }
    emitTelemetryEvent("payment_update_failed", {
      orderId: currentOrderId,
      cardIndex,
      ...context,
    });
  };

  const notifyPaymentNotClosed = (context = {}) => {
    if (!hasShownPaymentNotClosed) {
      message.warning(
        "התשלום התקבל אך ההזמנה לא נסגרה אוטומטית. נדרש טיפול."
      );
      setHasShownPaymentNotClosed(true);
    }
    emitTelemetryEvent("payment_done_not_closed", {
      orderId: currentOrderId,
      cardIndex,
      ...context,
    });
  };

  // 1. Auto open first card
  useEffect(() => {
    if (cardIndex === 0) {
      setExpandCard(true);
    }
  }, []);

  useEffect(() => {
    if (!paymentLinkOpen) return;
    const el = document.querySelector(".payment-link-iframe");
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [paymentLinkOpen, instantPayUrl]);

  useEffect(() => {
    if (!paymentLink) return;
    const currentCard = cards[cardIndex];
    const newNumberOfPayments = Number(paymentLink.tash);
    const newAmount = Number(paymentLink.actualAmount);
    const newPaid = paymentLink.paid;
    
    // Only update if values actually changed
    if (
      currentCard.numberofpayments === newNumberOfPayments &&
      currentCard.amount === newAmount &&
      currentCard.paid === newPaid
    ) {
      return;
    }
    
    const cardsTmp = [...cards];
    cardsTmp[cardIndex] = {
      ...currentCard,
      numberofpayments: newNumberOfPayments,
      amount: newAmount,
      paid: newPaid,
    };
    updateCards(cardsTmp);
  }, [paymentLink, cardIndex]);

  // Periodically check payment status when iframe is open
  useEffect(() => {
    if (!paymentLinkOpen || !paymentLink || !currentOrderId || paymentLink.paid) {
      return;
    }

    let previousPaidStatus = paymentLink.paid;
    let paymentCompleted = false;

    // Check payment status every 2 seconds while iframe is open
    const checkInterval = setInterval(() => {
      if (paymentCompleted || !paymentLinkOpen) {
        clearInterval(checkInterval);
        return;
      }

      fetchOrderPaymentSnapshot()
        .then((res) => {
          if (!res?.data) return;
          if (creditPaymentApi !== "dynamic" && res.data.entity_type === "proposal") return;

            if (sequentialMode) {
              const slot = resolvePaymentSlotFromOrder(res.data, cardIndex, paymentLink);
              if (isLdsPaymentSlotPaid(slot, res.data, cardIndex) && !paymentCompleted) {
                paymentCompleted = true;
                setConfirmingFromProvider(true);
                applyConfirmedPaidOrder(res.data);
                clearInterval(checkInterval);
              } else if (isLdsPaymentSlotFailed(slot)) {
                markSlotFailed("poll");
                clearInterval(checkInterval);
              }
              return;
            }

            // Find the current payment in the order
            const currentPayment = res.data.payments?.find(
              (p) =>
                p.amount === paymentLink.amount &&
                p.tash === paymentLink.tash
            );

            // Check if payment status changed
            if (currentPayment && currentPayment.paid !== previousPaidStatus) {
              previousPaidStatus = currentPayment.paid;

              if (currentPayment.paid && !paymentCompleted) {
                // Payment was completed - update order immediately
                paymentCompleted = true;
                
                // Get all paid cards from the order response
                const allPayments = res.data.payments || [];
                const paidPayments = allPayments.filter(p => p.paid);
                const totalAmountPaid = paidPayments.reduce((sum, p) => 
                  sum + (parseFloat(p.amount) || 0), 0
                );
                const totalNumberOfPayments = paidPayments.reduce((sum, p) => 
                  sum + (parseInt(p.tash) || 1), 0
                );
                const numberOfCreditCards = paidPayments.length;
                
                axios
                  .put(
                    ApiReqBaseUrl + `/orders/${currentOrderId}`,
                    {
                      totalAmountPaid: totalAmountPaid,
                      totalNumberOfPayments: totalNumberOfPayments,
                      numberOfCreditCards: numberOfCreditCards,
                    },
                    {
                      withCredentials: true,
                      headers: {
                        "Content-Type": "application/json",
                      },
                      validateStatus: () => true,
                    }
                  )
                  .then(async () => {
                    if (!currentOrderId) return;

                    try {
                      const priceResponse = await axios.get(
                        `${ApiReqBaseUrl}/orders/${currentOrderId}/price-calculations`,
                        {
                          withCredentials: true,
                          validateStatus: () => true,
                        }
                      );
                      if (
                        priceResponse.status >= 200 &&
                        priceResponse.status < 300
                      ) {
                        const priceData = priceResponse.data;
                        if (
                          priceData.status === "success" &&
                          priceData.data
                        ) {
                          const totalPriceNow =
                            priceData.data.now?.total_price || 0;
                          const priceDifference = Math.abs(
                            totalAmountPaid - totalPriceNow
                          );

                          if (priceDifference <= 2) {
                            const agentEmail = JSON.parse(
                              localStorage.getItem("UserInfo")
                            )?.email;
                            await axios.put(
                              ApiReqBaseUrl + `/orders/${currentOrderId}`,
                              {
                                status: "paid",
                                email: agentEmail,
                                sumpaid: totalAmountPaid,
                              },
                              {
                                withCredentials: true,
                                headers: {
                                  "Content-Type": "application/json",
                                },
                                validateStatus: () => true,
                              }
                            );
                          } else {
                            notifyPaymentNotClosed({
                              priceDifference,
                              totalAmountPaid,
                              totalPriceNow,
                            });
                          }
                        }
                      }
                    } catch (err) {
                      notifyPaymentUpdateFailure({
                        stage: "price_calc",
                        error: err?.message,
                      });
                    }
                  })
                  .catch((err) => {
                    console.error("Error updating order payment summary:", err);
                    notifyPaymentUpdateFailure({
                      stage: "update_summary",
                      error: err?.message,
                    });
                  });

                // Update payment link and close iframe
                setOrderReqData(res.data);
                setPaymentLinkOpen(false);
                message.success(t("notifications.paymentSuccessful"));
                clearInterval(checkInterval);
              }
            }
        })
        .catch((err) => {
          console.error('[CardPayment] Error checking payment status in iframe:', err);
        });
    }, 2000); // Check every 2 seconds

    // Cleanup interval after 10 minutes
    const timeoutId = setTimeout(() => {
      clearInterval(checkInterval);
    }, 600000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeoutId);
    };
  }, [
    paymentLinkOpen,
    paymentLink,
    currentOrderId,
    setOrderReqData,
    fetchOrderPaymentSnapshot,
    creditPaymentApi,
    t,
  ]);

  // Background polling when iframe is closed (prevents silent payment completion)
  useEffect(() => {
    if (paymentLinkOpen || !paymentLink || !currentOrderId || paymentLink.paid) {
      return;
    }

    let paymentCompleted = false;
    const checkInterval = setInterval(() => {
      if (paymentCompleted) {
        clearInterval(checkInterval);
        return;
      }

      fetchOrderPaymentSnapshot()
        .then((res) => {
          if (!res?.data) return;
          if (creditPaymentApi !== "dynamic" && res.data.entity_type === "proposal") return;
            if (sequentialMode) {
              const slot = resolvePaymentSlotFromOrder(res.data, cardIndex, paymentLink);
              if (isLdsPaymentSlotPaid(slot, res.data, cardIndex) && !paymentCompleted) {
                paymentCompleted = true;
                applyConfirmedPaidOrder(res.data);
              }
              return;
            }
            const currentPayment = res.data.payments?.find(
              (p) =>
                p.amount === paymentLink.amount &&
                p.tash === paymentLink.tash
            );
            if (currentPayment && currentPayment.paid && !paymentCompleted) {
              paymentCompleted = true;
              setOrderReqData(res.data);
              message.success(t("notifications.paymentSuccessful"));
            }
        })
        .catch((err) => {
          console.error("[CardPayment] Background polling error:", err);
        });
    }, 10000);

    const timeoutId = setTimeout(() => {
      clearInterval(checkInterval);
    }, 600000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeoutId);
    };
  }, [
    paymentLinkOpen,
    paymentLink,
    currentOrderId,
    setOrderReqData,
    fetchOrderPaymentSnapshot,
    creditPaymentApi,
    t,
  ]);

  // --- Functions
  const toggleExpantion = () => {
    setExpandCard(!expandCard);
  };

  // Mark and un mark inputs
  const handleMarkEdit = (e) => e.target?.classList.add("ci-input-editing");

  const handleUnMarkEdit = (e) =>
    e.target?.classList.remove("ci-input-editing");

  // Save changes for inputs
  const onInputChange = (e) => {
    handleInputChange(e, cards, updateCards, cardIndex);
  };

  const handlePaymentsChange = (e) => {
    const newPaymentsAmount = parseInt(e.target.value);
    let tempCards = cards;
    tempCards[cardIndex].numberofpayments = newPaymentsAmount;
    updateCards(tempCards);
  };

  const handlePayment = async (e) => {
    if (payLockRef.current) return;
    if (paymentTermsBlocking) {
      notifyTermsRequired();
      setPaymentLinkModalVisible(false);
      return;
    }
    if (!currentCard.amount || currentCard.amount <= 0) {
      console.warn('[CardPayment] Invalid amount:', currentCard.amount);
      message.warning(t("notifications.pleaseEnterAmount"));
      return;
    }

    if (splitPaymentError) {
      message.warning(splitPaymentError);
      return;
    }

    if (sequentialMode && paymentLink && !paymentLink.paid) {
      const paymentUrl = paymentLink?.paymentUrl || paymentLink?.url;
      if (!paymentUrl) {
        message.error(t("notifications.paymentLinkNotAvailable"));
        return;
      }
      payLockRef.current = true;
      setSlotFailed(false);
      onSequentialFailed?.(null);
      onSequentialProcessingChange?.(cardIndex);
      openPayInSameWindow(paymentUrl);
      return;
    }

    payLockRef.current = true;
    if (sequentialMode) {
      setSlotFailed(false);
      onSequentialFailed?.(null);
      onSequentialProcessingChange?.(cardIndex);
    }
    setSubmitLoading(true);
    try {
      let orderId = currentOrderId;
      if (typeof beforeCreatePaymentLink === "function") {
        const synced = await beforeCreatePaymentLink();
        const syncedId = synced?.id ?? synced?.ID ?? null;
        if (syncedId != null && String(syncedId).trim() !== "") {
          orderId = syncedId;
        }
      }
      if (!orderId) {
        console.error("[CardPayment] Missing order ID");
        message.error(t("notifications.orderNumberMissing"));
        setSubmitLoading(false);
        return;
      }

      const result = await executeLdsCreditPaymentLink({
        currentCard,
        orderId,
        selectedResult,
        paymentApi: creditPaymentApi,
        logContext: {
          handlerName: "CardPayment.handlePayment",
          cardIndex,
          sourceField:
            creditPaymentApi === "dynamic"
              ? "flight_agent_card_payment"
              : "lds_package_card_payment",
        },
      });

      if (result.ok && result.order) {
        setOrderReqData(result.order);
        // eslint-disable-next-line no-console
        console.info("CHAT_PAYMENT_URL_SET", {
          orderId: result.order?.id,
          paymentSlots: result.order?.payments?.length,
        });
        const payUrl =
          result.primaryPaymentUrl || getFirstUnpaidLdsPaymentUrl(result.order);
        if (instantOpenPay) {
          if (!payUrl) {
            message.error(t("notifications.paymentLinkNotAvailable"));
            payLockRef.current = false;
            onSequentialProcessingChange?.(null);
          } else {
            openPayInSameWindow(payUrl);
          }
        } else {
          message.success(t("notifications.paymentLinkCreated"));
        }
        trackMetaAddPaymentInfo(result.order, currentOrderId, {
          value: currentCard.amount,
        });
        setPaymentLinkModalVisible(false);
        setSubmitLoading(false);
        return;
      }

      if (result.errorCode === "PRICE_REFRESH") {
        const data = result.data;
        try {
          const { reconcileChatPaymentLockToOrderCanonical } = await import(
            "../../../stores/customerChatPaymentStore"
          );
          const canon = data?.canonical_price_ils;
          if (canon != null && Number.isFinite(Number(canon))) {
            reconcileChatPaymentLockToOrderCanonical(Number(canon));
          }
        } catch {
          /* ignore */
        }
        message.warning(
          data?.message ||
            "המחיר עודכן בהתאם להזמנה. לחצו שוב על כפתור התשלום."
        );
        payLockRef.current = false;
        onSequentialProcessingChange?.(null);
        setSubmitLoading(false);
        return;
      }
      if (result.errorCode === "PRICE_MISMATCH") {
        console.error("[CardPayment] PRICE_MISMATCH from server:", result.data);
        message.error(
          result.data?.message ||
            "אי-התאמה במחיר (PRICE_MISMATCH). פנו לנציג או נסו שוב."
        );
        setPaymentLinkModalVisible(false);
        payLockRef.current = false;
        onSequentialProcessingChange?.(null);
        setSubmitLoading(false);
        return;
      }

      if (result.errorCode === "PARSE_ERROR" || !result.errorMessage) {
        message.error(t("notifications.serverError", { status: result.status || 0, statusText: "" }));
      } else {
        message.error(result.errorMessage);
      }
      setPaymentLinkModalVisible(false);
      payLockRef.current = false;
      onSequentialProcessingChange?.(null);
    } catch (error) {
      console.error("[CardPayment] Error creating payment link:", error);
      const thrown = error?.message != null ? String(error.message).trim() : "";
      message.error(
        thrown && !/^[A-Z][A-Z0-9_]{3,}$/.test(thrown)
          ? thrown
          : t("notifications.errorCreatingPaymentLinkNetwork")
      );
      setPaymentLinkModalVisible(false);
      payLockRef.current = false;
      onSequentialProcessingChange?.(null);
    } finally {
      setSubmitLoading(false);
    }
    // setCurrentError(false);
    // setLoading(true);
    // pay(currentCard)
    //   .then((res) => {
    //     if (res) {
    //       let tempCards = JSON.parse(JSON.stringify(cards));
    //       tempCards[cardIndex].paid = true;
    //       updateCards(tempCards);
    //     }
    //   })
    //   .finally(() => {
    //     setLoading(false);
    //   });
    // setSubmitLoading(true);
  };

  const renderExpandedCard = () => {
    const defaultProps = {
      handleMarkEdit,
      handleUnMarkEdit,
      handleInputChange: onInputChange,
      disabled: cardDisabled,
    };
    
    const isButtonDisabled =
      !currentCard.amount ||
      currentCard.amount <= 0 ||
      !hasOrderOrSync ||
      submitLoading ||
      paymentTermsBlocking ||
      Boolean(splitPaymentError) ||
      payLockRef.current;
    
    return (
      <div className={`payment-card-details${sequentialMode ? " payment-card-details--sequential" : ""}`}>
        {currentCardPaid ? <CardPaidOverlay /> : null}

        {sequentialMode && slotFailed ? (
          <div className="seq-panel__error" role="alert">
            התשלום לא הושלם.
            <br />
            לא בוצע חיוב עבור שלב זה.
            <br />
            ניתן לנסות שוב.
          </div>
        ) : null}

        <PaymentsAmountInput
          {...defaultProps}
          value={parseNumber(currentCard?.amount || 0)}
          handlePaymentsChange={handlePaymentsChange}
          totalAmountWithPaymentsFee={currentCard.amountWithInterest}
          numberofpayments={currentCard.numberofpayments}
          disabled={paymentLink ? true : false}
          hideAmount={sequentialMode}
        />

        {currentCard.numberofpayments > 1 ? (
          <div className="ci-a-nop">
            <PaymentInterest currentCard={currentCard} />
          </div>
        ) : null}

        {/* Submit button */}
        <div className="ci-submit">
          {sequentialMode ? (
            submitLoading || confirmingFromProvider ? (
              <CustomLoading label={CREDIT_PAY_PROCESSING_LABEL} />
            ) : (
              <div
                className={classNames(
                  "credit-card-checkout-btn",
                  packageProfileCreditMarkers && "package-payment-credit-create-link",
                  packageProfileCreditMarkers && "package-payment-credit-open-pay"
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (paymentTermsBlocking) {
                    notifyTermsRequired();
                    return;
                  }
                  if (isButtonDisabled) return;
                  handlePayment(e);
                }}
                style={{
                  cursor: isButtonDisabled ? "not-allowed" : "pointer",
                  opacity: isButtonDisabled ? 0.6 : 1,
                }}
              >
                {createPayCtaLabel}
              </div>
            )
          ) : paymentLink ? (
            !paymentLink.paid ? (
              <div className="payment-buttons-container">
                <Button
                  className="payment-button-copy"
                  type="dashed"
                  disabled={paymentTermsBlocking}
                  onClick={() => {
                    if (paymentTermsBlocking) {
                      notifyTermsRequired();
                      return;
                    }
                    // copy payment url
                    navigator.clipboard.writeText(paymentLink.url);
                    message.success(t("notifications.paymentLinkCopied"));
                  }}
                  children="העתק קישור לתשלום"
                />
                <Button
                  className={classNames(
                    "payment-button-pay",
                    packageProfileCreditMarkers && "package-payment-credit-open-pay"
                  )}
                  type="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (paymentTermsBlocking) {
                      notifyTermsRequired();
                      return;
                    }

                    const paymentUrl = paymentLink?.paymentUrl || paymentLink?.url;
                    
                    if (!paymentUrl) {
                      message.error(t("notifications.paymentLinkNotAvailable"));
                      console.error('[CardPayment] Payment URL is missing:', paymentLink);
                      return;
                    }

                    openPayInSameWindow(paymentUrl);
                    trackMetaAddPaymentInfo(
                      selectedResult,
                      currentOrderId,
                      { value: currentCard.amount }
                    );
                  }}
                  children={CREDIT_PAY_NOW_LABEL}
                  disabled={paymentLink.paid || paymentTermsBlocking}
                />
                <Popconfirm
                  title="האם אתה בטוח שברצונך למחוק את התשלום?"
                  onConfirm={() => {
                    setIsRequesting(true);
                    const orderId = currentOrderId;
                    const rawDealType = String(
                      selectedResult?.deal_type || selectedResult?.dealtype || ""
                    ).trim();
                    const isCruiseOrder =
                      rawDealType === "Cruise" || !!selectedResult?.cruiseInfo;
                    const isOrderIdUuid =
                      typeof orderId === "string" &&
                      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
                        orderId
                      );
                    const ldsDealTypes = new Set([
                      "FlightsHotel",
                      "OrganizedTour",
                      "Dynamic",
                      "VacationPackage",
                      "Package",
                      "Flights",
                      "Flight",
                    ]);
                    const isLdsOrder =
                      !isCruiseOrder &&
                      (isOrderIdUuid || ldsDealTypes.has(rawDealType));
                    const endpoint = isCruiseOrder
                      ? "/removeCruisePaymentUrl"
                      : isLdsOrder
                        ? "/removeLDSPaymentUrl"
                        : "/removeHotelPaymentUrl";

                    const paymentUrlForDelete =
                      paymentLink?.paymentUrl ||
                      paymentLink?.url ||
                      paymentLink?.payment_url ||
                      null;
                    const payload = isCruiseOrder && paymentLink?.id
                      ? {
                          paymentId:
                            typeof paymentLink.id === "string"
                              ? parseInt(paymentLink.id, 10)
                              : paymentLink.id,
                          orderId,
                        }
                      : { paymentLink: paymentUrlForDelete, orderId };

                    console.log("[FE_REMOVE_PAYMENT_LINK_AUDIT]", {
                      orderIdSent: orderId,
                      orderNumber:
                        selectedResult?.orderid ||
                        selectedResult?.orderID ||
                        selectedResult?.dealid ||
                        null,
                      orderType: rawDealType || (isLdsOrder ? "LDS" : "Hotel"),
                      payload,
                      endpoint,
                    });

                    axios
                      .post(`${ApiReqBaseUrl}${endpoint}`, payload, {
                        headers: { "Content-Type": "application/json" },
                        validateStatus: () => true,
                      })
                      .then(async (res) => {
                        if (res.status === 200) {
                          const body = res.data || {};
                          const order = body?.order || body;
                          if (order && order.id) setOrderReqData(order);
                          message.success(t("notifications.paymentDeleted"));
                        } else {
                          message.error(t("notifications.errorDeletingPayment"));
                        }
                      })
                      .catch(() => {
                        message.error(t("notifications.errorDeletingPayment"));
                      })
                      .finally(() => {
                        setIsRequesting(false);
                      });
                  }}
                  okText="כן"
                  cancelText="לא"
                  getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                  overlayClassName="delete-payment-popconfirm"
                >
                  <Button
                    className="payment-button-delete"
                    type="primary"
                    danger
                    children="מחק תשלום"
                    loading={isRequesting}
                  />
                </Popconfirm>
              </div>
            ) : (
              <label>
                {t("paymentagent.payment-done")}
              </label>
            )
          ) : !submitLoading ? (
            <div
              className={classNames(
                "credit-card-checkout-btn",
                packageProfileCreditMarkers && "package-payment-credit-create-link"
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (paymentTermsBlocking) {
                  notifyTermsRequired();
                  return;
                }
                if (isButtonDisabled) {
                  return;
                }
                handlePayment(e);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
              }}
              style={{ 
                cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 99999,
                isolation: 'isolate',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                opacity: isButtonDisabled ? 0.6 : 1,
                userSelect: 'none'
              }}
            >
              {createPayCtaLabel}
            </div>
          ) : (
            <CustomLoading label={createPayLoadingLabel} />
          )}
        </div>

        {afterPayLinkSlot ? (
          <div className="credit-card-after-pay-link-slot">{afterPayLinkSlot}</div>
        ) : null}

        {/* Pics */}
        <PaymentIcons />

        {/* Error / Success messege */}
        <div className="ci-messeger">
          {/*<label*/}
          {/*  id={`messege-${cardIndex}-suc`}*/}
          {/*  className="ci-messeger-success"*/}
          {/*>*/}
          {/*  כרטיס האשראי עבר בהצלחה*/}
          {/*</label>*/}
          {!currentError ? null : (
            <label className="payment-error">
              <div> כרטיס האשראי לא עבר</div>
              <div>({currentError})</div>
            </label>
          )}
        </div>
        {/*</form>*/}
        {/*</div>*/}
      </div>
    );
  };

  const navigate = useNavigate();

  return (
    <>
      <div className={`splitPayment-wrapper${sequentialMode ? " splitPayment-wrapper--sequential" : ""}`}>
        {sequentialMode ? null : (
        <div
          className="splitPayment-card-extand-wrapper"
          onClick={toggleExpantion}
        >
          <div className="splitPayment-card-extand-item">
            {currentCardPaid && (
              <img
                className="splitPayment-card-extand-img"
                src={doneImg}
                alt="Done"
              ></img>
            )}
            <span className="splitPayment-card-extand-title">
              {t("paymentagent.cc")}: {cardIndex + 1}
            </span>
          </div>

          <div className="credit-card-left-topay-wrapper">
            {currentCardPaid && (
              <div className="credit-card-left-topay">
                <label>סה"כ שולם:</label>
                <label>{currentCard.amountWithInterest}</label>
              </div>
            )}

            <div className="splitPayment-card-extand-item">
              {!expandCard ? (
                <BsChevronDown></BsChevronDown>
              ) : (
                <BsChevronUp></BsChevronUp>
              )}
              {paymentLinkOpen && (
                <div
                  className="close-payment"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPaymentLinkOpen(false);
                  }}
                >
                  X
                </div>
              )}
            </div>
          </div>
        </div>
        )}
        {sequentialMode || expandCard
          ? paymentLinkOpen &&
            (instantPayUrl || paymentLink?.url || paymentLink?.paymentUrl) &&
            !paymentLink?.paid
            ? (
              <div className="sequential-iframe-wrap">
                {sequentialMode ? (
                  <button
                    type="button"
                    className="close-payment"
                    onClick={() => {
                      setPaymentLinkOpen(false);
                      payLockRef.current = false;
                      onSequentialProcessingChange?.(null);
                    }}
                  >
                    X
                  </button>
                ) : null}
                {confirmingFromProvider ? (
                  <div className="seq-confirm-overlay">{CREDIT_PAY_PROCESSING_LABEL}</div>
                ) : null}
                <iframe
                  className="payment-link-iframe"
                  title={`payment-link-${cardIndex}`}
                  key={instantPayUrl || paymentLink?.url || paymentLink?.paymentUrl || "pay"}
                  src={instantPayUrl || paymentLink?.url || paymentLink?.paymentUrl}
                  onLoad={(e) => {
                    try {
                      const href = e.currentTarget?.contentWindow?.location?.href;
                      const outcome = classifyHypIframeHref(href);
                      if (sequentialMode && (outcome === "fail" || outcome === "cancel")) {
                        markSlotFailed(outcome);
                        return;
                      }
                      if (sequentialMode && outcome === "success") {
                        setConfirmingFromProvider(true);
                      }
                    } catch {
                      /* cross-origin HYP iframe — poll order instead */
                    }
                    fetchOrderPaymentSnapshot().then((res) => {
                      if (!res?.data) return;
                      if (creditPaymentApi !== "dynamic" && res.data.entity_type === "proposal") return;
                      if (sequentialMode) {
                        applyConfirmedPaidOrder(res.data);
                        return;
                      }
                      const payment = res.data.payments?.find(
                        (p) =>
                          p.amount === paymentLink?.amount &&
                          p.tash === paymentLink?.tash &&
                          p.paid !== paymentLink?.paid
                      );
                      if (payment && payment.paid) {
                        const allPayments = res.data.payments || [];
                        const paidPayments = allPayments.filter((p) => p.paid);
                        const totalAmountPaid = paidPayments.reduce(
                          (sum, p) => sum + (parseFloat(p.amount) || 0),
                          0
                        );
                        const totalNumberOfPayments = paidPayments.reduce(
                          (sum, p) => sum + (parseInt(p.tash, 10) || 1),
                          0
                        );
                        axios
                          .put(
                            ApiReqBaseUrl + `/orders/${currentOrderId}`,
                            {
                              totalAmountPaid,
                              totalNumberOfPayments,
                              numberOfCreditCards: paidPayments.length,
                            },
                            {
                              withCredentials: true,
                              headers: { "Content-Type": "application/json" },
                              validateStatus: () => true,
                            }
                          )
                          .catch((err) => {
                            console.error("Error updating order payment summary:", err);
                          });
                        setOrderReqData(res.data);
                      }
                    });
                  }}
                />
              </div>
            )
            : renderExpandedCard()
          : null}
      </div>
    </>
  );
};

export default CardPayment;
